import { useState } from 'react';
import { useAIAgents } from '@/hooks/useAIAgents';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AgentDetailDialog } from './AgentDetailDialog';
import { Brain, Play, Pause, MoreHorizontal, Settings, Trash2, Activity } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export const AgentList = () => {
  const { agents, isLoading, updateAgent, deleteAgent, executeAgent } = useAIAgents();
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);

  const handleStatusToggle = (agentId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    updateAgent({ id: agentId, updates: { status: newStatus } });
  };

  const handleExecute = (agentId: string) => {
    executeAgent({ agentId });
  };

  const handleDelete = (agentId: string) => {
    if (confirm('Are you sure you want to delete this agent?')) {
      deleteAgent(agentId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'error':
        return 'bg-red-500';
      case 'training':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getAgentTypeLabel = (type: string) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-muted rounded w-3/4"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-12">
        <Brain className="mx-auto h-12 w-12 text-muted-foreground" />
        <h3 className="mt-4 text-lg font-medium">No agents yet</h3>
        <p className="text-muted-foreground">
          Create your first AI agent to start automating tasks.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {agents.map((agent) => (
          <Card key={agent.id} className="relative">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(agent.status)}`} />
                  <div>
                    <CardTitle className="text-base">{agent.name}</CardTitle>
                    <CardDescription className="text-sm">
                      {getAgentTypeLabel(agent.agent_type)}
                    </CardDescription>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => setSelectedAgent(agent.id)}>
                      <Settings className="mr-2 h-4 w-4" />
                      Configure
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExecute(agent.id)}>
                      <Play className="mr-2 h-4 w-4" />
                      Execute Now
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => handleDelete(agent.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {agent.description && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {agent.description}
                </p>
              )}
              
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <Badge variant="secondary" className="ml-2">
                    {agent.status}
                  </Badge>
                </div>
                <Switch
                  checked={agent.status === 'active'}
                  onCheckedChange={() => handleStatusToggle(agent.id, agent.status)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Executions:</span>
                  <div className="font-medium">{agent.execution_count}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Success Rate:</span>
                  <div className="font-medium">{Math.round(agent.success_rate)}%</div>
                </div>
              </div>

              {agent.last_execution_at && (
                <div className="text-sm">
                  <span className="text-muted-foreground">Last run:</span>
                  <div className="text-xs">
                    {formatDistanceToNow(new Date(agent.last_execution_at))} ago
                  </div>
                </div>
              )}

              {agent.is_scheduled && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Activity className="mr-1 h-3 w-3" />
                  Scheduled
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedAgent && (
        <AgentDetailDialog
          agentId={selectedAgent}
          open={true}
          onOpenChange={(open) => !open && setSelectedAgent(null)}
        />
      )}
    </>
  );
};